/**
 * AlertDialog — shadcn-style wrapper around Radix AlertDialog.
 *
 * The lower-level building block for `ConfirmDialog`. Use that instead for
 * yes/no confirmations; reach for these primitives directly only when you
 * need a non-standard confirm shape (e.g. confirm-with-typed-name).
 *
 * AlertDialog differs from Dialog in two ways the underlying Radix package
 * enforces: (1) the focus trap is more strict, (2) the dialog only closes
 * via explicit Cancel/Action buttons (no escape-to-dismiss). Both match
 * the "are you sure?" pattern.
 */
import * as React from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { cn } from "../lib/cn.js";

export const AlertDialogRoot = AlertDialog.Root;
export const AlertDialogTrigger = AlertDialog.Trigger;
export const AlertDialogPortal = AlertDialog.Portal;

export const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialog.Overlay>
>(function AlertDialogOverlay({ className, ...props }, ref) {
  return (
    <AlertDialog.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 bg-ink/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
});

export interface AlertDialogContentProps extends React.ComponentPropsWithoutRef<
  typeof AlertDialog.Content
> {
  size?: "sm" | "md";
}

const SIZES: Record<NonNullable<AlertDialogContentProps["size"]>, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
};

export const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialog.Content>,
  AlertDialogContentProps
>(function AlertDialogContent({ className, size = "md", ...props }, ref) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialog.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-paper p-5 shadow-xl outline-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          SIZES[size],
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
});

export function AlertDialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col gap-1.5 mb-2", className)} {...props} />;
}

export const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialog.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialog.Title>
>(function AlertDialogTitle({ className, ...props }, ref) {
  return (
    <AlertDialog.Title
      ref={ref}
      className={cn("text-base font-semibold text-ink leading-tight", className)}
      {...props}
    />
  );
});

export const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialog.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialog.Description>
>(function AlertDialogDescription({ className, ...props }, ref) {
  return (
    <AlertDialog.Description
      ref={ref}
      className={cn("text-sm text-muted leading-relaxed", className)}
      {...props}
    />
  );
});

export function AlertDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2", className)}
      {...props}
    />
  );
}

export const AlertDialogAction = AlertDialog.Action;
export const AlertDialogCancel = AlertDialog.Cancel;
